import React, { useState, useEffect } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Progress } from '@/components/ui/progress';
import { Badge } from '@/components/ui/badge';
import { useAuth } from '@/contexts/AuthContext';
import { toast } from 'sonner';
import { motion, AnimatePresence } from 'framer-motion';
import Confetti from 'react-confetti';
import {
  ArrowLeft, ArrowRight, Shield, CheckCircle, XCircle, Lock,
  Eye, Users, Heart, AlertTriangle, Key, Footprints, Ban,
  Star, Trophy, Loader2, Volume2
} from 'lucide-react';
import { speak as _speakTTS, stopSpeaking as _stopTTS } from '@/utils/speak';

const DigitalSafetyLesson = () => {
  const { childId } = useParams();
  const navigate = useNavigate();
  const { api } = useAuth();
  
  const [loading, setLoading] = useState(true);
  const [lesson, setLesson] = useState(null);
  const [phase, setPhase] = useState('learn'); // learn, quiz, results
  const [currentConcept, setCurrentConcept] = useState(0);
  const [currentQuestion, setCurrentQuestion] = useState(0);
  const [selectedAnswer, setSelectedAnswer] = useState(null);
  const [answers, setAnswers] = useState([]);
  const [results, setResults] = useState(null);
  const [showConfetti, setShowConfetti] = useState(false);

  const iconMap = {
    '🔒': Lock,
    '👨‍👩‍👧': Users,
    '💝': Heart,
    '🎭': AlertTriangle,
    '🛡️': Shield,
    '🖱️': Eye,
    '👣': Footprints,
    '🚫': Ban,
    '🔑': Key,
  };

  useEffect(() => {
    fetchLesson();
  }, [childId]);

  const fetchLesson = async () => {
    try {
      const response = await api.get(`/digital-safety/lesson/${childId}`);
      setLesson(response.data);
      
      if (response.data.completed) {
        // Already completed, redirect
        toast.success('You already completed Digital Safety training!');
        navigate(`/learn/${childId}`);
      }
    } catch (error) {
      console.error('Failed to fetch lesson:', error);
      toast.error('Failed to load Digital Safety lesson');
    } finally {
      setLoading(false);
    }
  };

  const speakText = (text) => {
    _speakTTS(text, 0.9);
  };

  const nextConcept = () => {
    if (currentConcept < lesson.content.key_concepts.length - 1) {
      setCurrentConcept(prev => prev + 1);
    } else {
      setPhase('quiz');
      setCurrentQuestion(0);
    }
  };

  const prevConcept = () => {
    if (currentConcept > 0) {
      setCurrentConcept(prev => prev - 1);
    }
  };

  const submitAnswer = () => {
    if (!selectedAnswer) return;
    
    const newAnswers = [...answers, {
      question_index: currentQuestion,
      answer: selectedAnswer
    }];
    setAnswers(newAnswers);
    
    if (currentQuestion < lesson.quiz.length - 1) {
      setCurrentQuestion(prev => prev + 1);
      setSelectedAnswer(null);
    } else {
      // Submit quiz
      completeQuiz(newAnswers);
    }
  };

  const completeQuiz = async (allAnswers) => {
    try {
      const response = await api.post(`/digital-safety/complete/${childId}`, {
        answers: allAnswers
      });
      
      setResults(response.data);
      setPhase('results');
      
      if (response.data.passed) {
        setShowConfetti(true);
        setTimeout(() => setShowConfetti(false), 5000);
        toast.success('Congratulations! You\'re now a Digital Safety Champion!');
        
        // Auto-complete the daily task if this lesson was started from a daily task
        const activeTaskId = localStorage.getItem('activeTaskId');
        const activeTaskModule = localStorage.getItem('activeTaskModule');
        
        if (activeTaskId && activeTaskModule === 'digital_safety') {
          try {
            await api.post(`/daily-tasks/${activeTaskId}/complete`);
            toast.success('Daily task completed! +XP earned!', {
              icon: '🎯',
              duration: 3000
            });
            localStorage.removeItem('activeTaskId');
            localStorage.removeItem('activeTaskModule');
          } catch (taskError) {
            console.log('Task may already be completed:', taskError);
          }
        }
      }
    } catch (error) {
      console.error('Failed to submit quiz:', error);
      toast.error('Failed to submit quiz');
    }
  };

  const retryQuiz = () => {
    setPhase('learn');
    setCurrentConcept(0);
    setCurrentQuestion(0);
    setSelectedAnswer(null);
    setAnswers([]);
    setResults(null);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-slate-900 to-slate-800 flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-12 h-12 animate-spin text-cyan-500 mx-auto mb-4" />
          <p className="text-slate-300">Loading Digital Safety lesson...</p>
        </div>
      </div>
    );
  }

  if (!lesson) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-slate-900 to-slate-800 flex items-center justify-center">
        <Card className="bg-slate-800/50 border-slate-700 text-white">
          <CardContent className="p-8 text-center">
            <AlertTriangle className="w-12 h-12 text-amber-500 mx-auto mb-4" />
            <p>Unable to load lesson. Please try again.</p>
            <Button onClick={fetchLesson} className="mt-4">Retry</Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  const concept = lesson.content.key_concepts[currentConcept];
  const ConceptIcon = iconMap[concept?.icon] || Shield;

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-900 via-indigo-900 to-slate-900">
      {showConfetti && <Confetti recycle={false} numberOfPieces={400} />}
      
      {/* Header */}
      <header className="bg-gradient-to-r from-cyan-600 to-blue-600 text-white py-4 px-4 sticky top-0 z-40">
        <div className="max-w-4xl mx-auto flex items-center justify-between">
          <Link to={`/learn/${childId}`} data-testid="back-to-learning">
            <Button variant="ghost" size="icon" className="text-white hover:bg-white/20">
              <ArrowLeft className="w-5 h-5" />
            </Button>
          </Link>
          
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-white/20 rounded-xl flex items-center justify-center">
              <Shield className="w-6 h-6" />
            </div>
            <div>
              <h1 className="font-bold text-lg">{lesson.title}</h1>
              <p className="text-xs text-white/80">Mandatory Safety Training</p>
            </div>
          </div>
          
          <Badge className="bg-amber-500 text-white border-0">
            {lesson.age_group}
          </Badge>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 py-6">
        <AnimatePresence mode="wait">
          {/* LEARNING PHASE */}
          {phase === 'learn' && (
            <motion.div
              key="learn"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              className="space-y-6"
            >
              {/* Progress */}
              <Card className="bg-slate-800/50 border-slate-700">
                <CardContent className="p-4">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm text-slate-300">
                      Lesson {currentConcept + 1} of {lesson.content.key_concepts.length}
                    </span>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => speakText(concept.explanation)}
                      className="text-cyan-400 hover:text-cyan-300"
                    >
                      <Volume2 className="w-4 h-4 mr-1" />
                      Read Aloud
                    </Button>
                  </div>
                  <Progress 
                    value={((currentConcept + 1) / lesson.content.key_concepts.length) * 100} 
                    className="h-2 bg-slate-700"
                  />
                </CardContent>
              </Card>

              {/* Introduction (first concept only) */}
              {currentConcept === 0 && (
                <Card className="bg-gradient-to-br from-cyan-900/50 to-blue-900/50 border-cyan-700/50">
                  <CardContent className="p-6">
                    <p className="text-slate-200 text-lg leading-relaxed">
                      {lesson.content.introduction}
                    </p>
                  </CardContent>
                </Card>
              )}

              {/* Current Concept */}
              <Card className="bg-slate-800/50 border-slate-700 overflow-hidden" data-testid="safety-concept-card">
                <div className="bg-gradient-to-r from-cyan-600 to-blue-600 p-6">
                  <div className="flex items-center gap-4">
                    <div className="w-16 h-16 bg-white/20 rounded-2xl flex items-center justify-center">
                      <ConceptIcon className="w-10 h-10 text-white" />
                    </div>
                    <div>
                      <h2 className="text-2xl font-bold text-white">{concept.concept}</h2>
                      <p className="text-cyan-100 text-sm mt-1">{concept.icon} Safety Rule #{currentConcept + 1}</p>
                    </div>
                  </div>
                </div>
                
                <CardContent className="p-6 space-y-4">
                  <p className="text-slate-200 text-lg leading-relaxed">
                    {concept.explanation}
                  </p>
                  
                  {concept.example && (
                    <div className="bg-amber-900/30 border border-amber-700/50 rounded-xl p-4">
                      <h3 className="font-semibold text-amber-400 mb-2 flex items-center gap-2">
                        <Star className="w-4 h-4" />
                        Example
                      </h3>
                      <p className="text-amber-100">{concept.example}</p>
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* Tips (show on last concept) */}
              {currentConcept === lesson.content.key_concepts.length - 1 && (
                <Card className="bg-emerald-900/30 border-emerald-700/50">
                  <CardHeader>
                    <CardTitle className="text-emerald-400 flex items-center gap-2">
                      <CheckCircle className="w-5 h-5" />
                      Remember These Tips!
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <ul className="space-y-2">
                      {lesson.content.tips.map((tip, idx) => (
                        <li key={idx} className="flex items-start gap-2 text-emerald-100">
                          <CheckCircle className="w-4 h-4 text-emerald-400 mt-0.5 flex-shrink-0" />
                          <span>{tip}</span>
                        </li>
                      ))}
                    </ul>
                  </CardContent>
                </Card>
              )}

              {/* Navigation */}
              <div className="flex gap-3">
                <Button
                  variant="outline"
                  onClick={prevConcept}
                  disabled={currentConcept === 0}
                  className="flex-1 border-slate-600 text-slate-300 hover:bg-slate-700"
                >
                  <ArrowLeft className="w-4 h-4 mr-2" />
                  Previous
                </Button>
                <Button
                  onClick={nextConcept}
                  className="flex-1 bg-gradient-to-r from-cyan-600 to-blue-600"
                  data-testid="next-concept-btn"
                >
                  {currentConcept < lesson.content.key_concepts.length - 1 ? (
                    <>
                      Next Lesson
                      <ArrowRight className="w-4 h-4 ml-2" />
                    </>
                  ) : (
                    <>
                      Take Safety Quiz
                      <Trophy className="w-4 h-4 ml-2" />
                    </>
                  )}
                </Button>
              </div>
            </motion.div>
          )}

          {/* QUIZ PHASE */}
          {phase === 'quiz' && (
            <motion.div
              key="quiz"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              className="space-y-6"
            >
              {/* Progress */}
              <Card className="bg-slate-800/50 border-slate-700">
                <CardContent className="p-4">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm text-slate-300">
                      Question {currentQuestion + 1} of {lesson.quiz.length}
                    </span>
                    <Badge className="bg-cyan-600 text-white">Safety Quiz</Badge>
                  </div>
                  <Progress 
                    value={((currentQuestion + 1) / lesson.quiz.length) * 100} 
                    className="h-2 bg-slate-700"
                  />
                </CardContent>
              </Card>

              {/* Question */}
              <Card className="bg-slate-800/50 border-slate-700" data-testid="quiz-question-card">
                <CardContent className="p-6">
                  <h2 className="text-xl font-semibold text-white mb-6">
                    {lesson.quiz[currentQuestion].question}
                  </h2>
                  
                  <div className="space-y-3">
                    {lesson.quiz[currentQuestion].options.map((option, idx) => (
                      <button
                        key={idx}
                        onClick={() => setSelectedAnswer(option)}
                        className={`w-full text-left p-4 rounded-xl border-2 transition-all ${
                          selectedAnswer === option 
                            ? 'border-cyan-500 bg-cyan-900/30' 
                            : 'border-slate-600 hover:border-slate-500 hover:bg-slate-700/50'
                        }`}
                        data-testid={`quiz-option-${idx}`}
                      >
                        <span className="text-slate-200">{option}</span>
                      </button>
                    ))}
                  </div>

                  <Button
                    onClick={submitAnswer}
                    disabled={!selectedAnswer}
                    className="w-full mt-6 bg-gradient-to-r from-cyan-600 to-blue-600"
                    data-testid="submit-quiz-answer-btn"
                  >
                    {currentQuestion < lesson.quiz.length - 1 ? 'Next Question' : 'Finish Quiz'}
                    <ArrowRight className="w-4 h-4 ml-2" />
                  </Button>
                </CardContent>
              </Card>
            </motion.div>
          )}

          {/* RESULTS PHASE */}
          {phase === 'results' && results && (
            <motion.div
              key="results"
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              className="space-y-6"
            >
              <Card className="bg-slate-800/50 border-slate-700 overflow-hidden" data-testid="quiz-results-card">
                <div className={`p-8 text-center ${
                  results.passed 
                    ? 'bg-gradient-to-r from-emerald-600 to-cyan-600' 
                    : 'bg-gradient-to-r from-amber-600 to-orange-600'
                }`}>
                  {results.passed ? (
                    <>
                      <Trophy className="w-20 h-20 mx-auto mb-4 text-amber-300" />
                      <h2 className="text-3xl font-bold text-white mb-2">
                        Digital Safety Champion!
                      </h2>
                      <p className="text-white/80">You're now ready to explore and learn safely!</p>
                    </>
                  ) : (
                    <>
                      <Shield className="w-20 h-20 mx-auto mb-4 text-white/80" />
                      <h2 className="text-3xl font-bold text-white mb-2">
                        Almost There!
                      </h2>
                      <p className="text-white/80">Let's review the safety rules and try again.</p>
                    </>
                  )}
                </div>
                
                <CardContent className="p-6 space-y-6">
                  {/* Score */}
                  <div className="grid grid-cols-2 gap-4 text-center">
                    <div className={`p-4 rounded-xl ${
                      results.passed ? 'bg-emerald-900/30' : 'bg-amber-900/30'
                    }`}>
                      <p className="text-4xl font-bold text-white">{results.score}%</p>
                      <p className="text-sm text-slate-400">Your Score</p>
                    </div>
                    <div className="bg-slate-700/50 p-4 rounded-xl">
                      <p className="text-4xl font-bold text-white">{results.correct}/{results.total}</p>
                      <p className="text-sm text-slate-400">Correct Answers</p>
                    </div>
                  </div>

                  {/* XP Earned */}
                  {results.passed && results.xp_earned > 0 && (
                    <div className="bg-amber-900/30 border border-amber-700/50 rounded-xl p-4 text-center">
                      <div className="flex items-center justify-center gap-2 text-amber-400">
                        <Star className="w-6 h-6 fill-amber-400" />
                        <span className="text-2xl font-bold">+{results.xp_earned} XP</span>
                        <Star className="w-6 h-6 fill-amber-400" />
                      </div>
                      <p className="text-amber-200 text-sm mt-1">Safety Training Bonus!</p>
                    </div>
                  )}

                  {/* Results Detail */}
                  <div className="space-y-3">
                    <h3 className="font-semibold text-slate-300">Your Answers:</h3>
                    {results.results.map((result, idx) => (
                      <div 
                        key={idx}
                        className={`p-4 rounded-xl border ${
                          result.correct 
                            ? 'bg-emerald-900/20 border-emerald-700/50' 
                            : 'bg-red-900/20 border-red-700/50'
                        }`}
                      >
                        <div className="flex items-start gap-3">
                          {result.correct ? (
                            <CheckCircle className="w-5 h-5 text-emerald-400 mt-0.5 flex-shrink-0" />
                          ) : (
                            <XCircle className="w-5 h-5 text-red-400 mt-0.5 flex-shrink-0" />
                          )}
                          <div className="flex-1">
                            <p className="text-slate-200 font-medium">{result.question}</p>
                            {!result.correct && (
                              <p className="text-emerald-400 text-sm mt-1">
                                Correct: {result.correct_answer}
                              </p>
                            )}
                            <p className="text-slate-400 text-sm mt-2">{result.explanation}</p>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>

              {/* Action Buttons */}
              <div className="flex gap-3">
                {results.passed ? (
                  <Button
                    onClick={() => navigate(`/learn/${childId}`)}
                    className="w-full bg-gradient-to-r from-emerald-600 to-cyan-600"
                    data-testid="start-learning-btn"
                  >
                    <Trophy className="w-4 h-4 mr-2" />
                    Start Learning!
                  </Button>
                ) : (
                  <>
                    <Button
                      variant="outline"
                      onClick={retryQuiz}
                      className="flex-1 border-slate-600 text-slate-300 hover:bg-slate-700"
                      data-testid="review-lessons-btn"
                    >
                      <ArrowLeft className="w-4 h-4 mr-2" />
                      Review Lessons
                    </Button>
                    <Button
                      onClick={retryQuiz}
                      className="flex-1 bg-gradient-to-r from-cyan-600 to-blue-600"
                      data-testid="retry-quiz-btn"
                    >
                      Try Again
                      <ArrowRight className="w-4 h-4 ml-2" />
                    </Button>
                  </>
                )}
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </main>
    </div>
  );
};

export default DigitalSafetyLesson;
